// ============================================================
// nucleo/respaldo.jsx — sacar todo a un archivo y volverlo a meter.
//
// Hoy los datos viven en el navegador de UN teléfono, amarrados a la
// dirección con la que entraste. Si el teléfono se moja, si alguien borra
// los datos del navegador, o si cambia la dirección del túnel, se pierde
// todo. No hay papelera ni "deshacer".
//
// Mientras no exista el servidor, esto es el único seguro que hay, y por eso
// va en el nivel gratis: perder el inventario no debería costar una
// suscripción.
//
// El archivo es texto plano legible. A propósito: se puede abrir, revisar y
// hasta corregir a mano. Un respaldo que no puedes leer no es un respaldo,
// es un acto de fe.
// ============================================================

import React, { useState, useRef } from "react";
import { Download, Upload, AlertTriangle, Check, X, Save } from "lucide-react";
import { COL, tarjeta } from "./tema.jsx";
import { bitacora } from "./bitacora.jsx";

export const VERSION_RESPALDO = 1;

// Las piezas que se guardan. Si mañana hay una más, se agrega AQUÍ y queda
// respaldada en todos lados: no hay que acordarse de tocar tres archivos.
// El libro va en la lista Y NO ES OPCIONAL. Sin él, restaurar un respaldo
// devuelve los productos con su número pero sin historia, y lo que se pierde
// son los movimientos que todavía no se habían subido: ventas que existían
// nada más en este teléfono.
export const PIEZAS = ["nivel", "productos", "ventas", "clientes", "gastos",
  "cobro", "cuentas", "secciones", "libro"];

// ------------------------------------------------------------
// Armar el respaldo. Función pura: se prueba sin navegador.
// ------------------------------------------------------------
export function armarRespaldo(estado = {}, negocio = "Mi negocio") {
  const datos = {};
  for (const k of PIEZAS) if (estado[k] !== undefined) datos[k] = estado[k];
  return {
    app: "mi-negocio",
    version: VERSION_RESPALDO,
    negocio,
    cuando: new Date().toISOString(),
    resumen: {
      productos: (estado.productos || []).length,
      ventas: (estado.ventas || []).length,
      cuentas: (estado.cuentas || []).length,
      clientes: (estado.clientes || []).length,
    },
    datos,
  };
}

// ------------------------------------------------------------
// Leer un respaldo. Esto viene de FUERA, así que se desconfía de todo:
// puede ser otro archivo, estar cortado, o traer basura. Nunca truena;
// devuelve qué salió mal en palabras que el vendedor entienda.
// ------------------------------------------------------------
export function leerRespaldo(texto) {
  let j;
  try {
    j = JSON.parse(String(texto || ""));
  } catch (e) {
    return { ok: false, problema: "Ese archivo no se puede leer. ¿Seguro que es un respaldo de la app?" };
  }
  if (!j || typeof j !== "object" || Array.isArray(j)) {
    return { ok: false, problema: "El archivo está vacío o no tiene la forma de un respaldo." };
  }
  if (j.app !== "mi-negocio") {
    return { ok: false, problema: "Ese respaldo es de otra aplicación." };
  }
  if (Number(j.version) > VERSION_RESPALDO) {
    return { ok: false, problema: "Ese respaldo viene de una versión más nueva de la app. Actualiza primero." };
  }
  if (!j.datos || typeof j.datos !== "object") {
    return { ok: false, problema: "El respaldo no trae datos adentro." };
  }

  // Solo se aceptan las piezas conocidas, y solo con el tipo correcto.
  // Un respaldo manipulado no debe poder meter cosas raras al estado.
  const estado = {};
  for (const k of PIEZAS) {
    const v = j.datos[k];
    if (v === undefined) continue;
    if (k === "nivel") { if (typeof v === "string") estado[k] = v; continue; }
    if (k === "cobro") { if (v && typeof v === "object" && !Array.isArray(v)) estado[k] = v; continue; }
    // El libro es un objeto, no una lista, y por eso necesita su propio
    // renglón: con la regla de "solo listas" se caía al suelo sin avisar y el
    // respaldo se restauraba sin las ventas que faltaba subir.
    if (k === "libro") {
      if (v && typeof v === "object" && !Array.isArray(v) && Array.isArray(v.movs)) {
        const obj = (x) => (x && typeof x === "object" && !Array.isArray(x)) ? x : {};
        estado[k] = {
          base: obj(v.base), saldos: obj(v.saldos), ids: obj(v.ids),
          movs: v.movs.filter((m) => m && typeof m === "object" && m.id),
        };
      }
      continue;
    }
    if (Array.isArray(v)) estado[k] = v;
  }
  if (Object.keys(estado).length === 0) {
    return { ok: false, problema: "El respaldo no trae nada que se pueda restaurar." };
  }

  return {
    ok: true,
    estado,
    cuando: j.cuando || null,
    negocio: j.negocio || "Mi negocio",
    resumen: {
      productos: (estado.productos || []).length,
      ventas: (estado.ventas || []).length,
      cuentas: (estado.cuentas || []).length,
      clientes: (estado.clientes || []).length,
    },
  };
}

// Nombre del archivo: con fecha, para que se puedan tener varios y se
// entienda cuál es cuál sin abrirlos.
export function nombreArchivo(cuando = new Date()) {
  const d = cuando instanceof Date ? cuando : new Date(cuando);
  const p = (n) => String(n).padStart(2, "0");
  return `mi-negocio-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}.json`;
}

// ------------------------------------------------------------
// La parte que sí usa el navegador.
// ------------------------------------------------------------
export function bajarRespaldo(estado, negocio) {
  try {
    const r = armarRespaldo(estado, negocio);
    const texto = JSON.stringify(r, null, 2);
    const url = URL.createObjectURL(new Blob([texto], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nombreArchivo();
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    bitacora.paso("respaldo", "respaldo guardado", r.resumen);
    return true;
  } catch (e) {
    bitacora.error("respaldo", "no se pudo guardar el respaldo", e);
    return false;
  }
}

// ------------------------------------------------------------
// La pantalla. Dos botones y una advertencia que no se puede saltar.
// ------------------------------------------------------------
export default function Respaldo({ estado, negocio = "Mi negocio", onRestaurar }) {
  const [listo, setListo] = useState(false);
  const [hallado, setHallado] = useState(null);   // respaldo leído, esperando confirmación
  const [problema, setProblema] = useState(null);
  const archivo = useRef(null);

  const cuantos = (estado?.productos || []).length;

  const guardar = () => {
    const ok = bajarRespaldo(estado, negocio);
    if (ok) { setListo(true); setTimeout(() => setListo(false), 2500); }
    else setProblema("No se pudo guardar el archivo. Quedó apuntado en la bitácora.");
  };

  const escoger = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setProblema(null);
    const fr = new FileReader();
    fr.onload = () => {
      const r = leerRespaldo(String(fr.result));
      if (!r.ok) { setProblema(r.problema); bitacora.aviso("respaldo", "respaldo rechazado", r.problema); return; }
      setHallado(r);
    };
    fr.onerror = () => setProblema("No se pudo abrir el archivo.");
    fr.readAsText(f);
    e.target.value = "";          // deja volver a escoger el mismo archivo
  };

  return (
    <div style={{ ...tarjeta, padding: 14, marginTop: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4, display: "flex", alignItems: "center", gap: 7 }}>
        <Save size={17} color={COL.kraft} /> Respaldo
      </div>
      <div style={{ fontSize: 11.5, color: COL.kraft, fontWeight: 600, lineHeight: 1.4, marginBottom: 11 }}>
        {cuantos > 0
          ? `Tienes ${cuantos} ${cuantos === 1 ? "producto" : "productos"} guardados sólo en este teléfono. Si se pierde, no hay de dónde sacarlos.`
          : "Todavía no hay nada que respaldar, pero cuando lo haya, aquí se guarda."}
      </div>

      <button onClick={guardar} disabled={cuantos === 0}
        style={{ width: "100%", padding: 13, borderRadius: 12, cursor: cuantos ? "pointer" : "default",
          fontFamily: "inherit", border: "none",
          background: listo ? COL.billete : cuantos ? COL.tinta : COL.kraft,
          color: listo ? "#fff" : COL.papel, fontSize: 13.5, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {listo ? <Check size={17} /> : <Download size={17} />}
        {listo ? "Guardado en tus descargas" : "Guardar respaldo en un archivo"}
      </button>

      <label htmlFor="respaldo-archivo"
        style={{ width: "100%", marginTop: 8, padding: 13, borderRadius: 12, cursor: "pointer",
          boxSizing: "border-box", border: `2px solid ${COL.kraft}`, background: "transparent",
          color: COL.tinta, fontSize: 13.5, fontWeight: 800, display: "flex",
          alignItems: "center", justifyContent: "center", gap: 8 }}>
        <Upload size={17} color={COL.kraft} /> Restaurar de un archivo
      </label>
      <input id="respaldo-archivo" type="file" accept="application/json,.json" onChange={escoger}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />

      {problema && (
        <div style={{ background: "#F7E4E0", borderRadius: 11, padding: "10px 12px", marginTop: 10,
          fontSize: 12, fontWeight: 700, color: COL.chile, lineHeight: 1.4,
          display: "flex", gap: 8, alignItems: "flex-start" }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {problema}
        </div>
      )}

      {hallado && <ModalRestaurar hallado={hallado} cuantosHoy={cuantos}
        onCancel={() => setHallado(null)}
        onOk={() => { onRestaurar(hallado.estado);
          bitacora.paso("respaldo", "respaldo restaurado", hallado.resumen);
          setHallado(null); }} />}
    </div>
  );
}

// Restaurar BORRA lo que hay. Eso se dice claro y se cuenta cuánto se pierde:
// nadie debería enterarse después.
function ModalRestaurar({ hallado, cuantosHoy, onCancel, onOk }) {
  const fecha = hallado.cuando ? String(hallado.cuando).slice(0, 10) : "sin fecha";
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(32,20,10,0.5)", zIndex: 45,
      display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: COL.crema, width: "100%", maxWidth: 560,
        borderRadius: "24px 24px 0 0", padding: "20px 18px calc(20px + env(safe-area-inset-bottom))",
        maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: COL.kraft, borderRadius: 4, margin: "0 auto 16px", opacity: 0.5 }} />

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 19, fontWeight: 800, flex: 1 }}>Restaurar el respaldo</div>
          <button onClick={onCancel} aria-label="Cerrar"
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4 }}>
            <X size={20} color={COL.kraft} />
          </button>
        </div>

        <div style={{ background: COL.papel, borderRadius: 12, padding: 12, marginTop: 8,
          fontSize: 12.5, fontWeight: 700, lineHeight: 1.5 }}>
          <div>{`Del ${fecha}, de ${hallado.negocio}`}</div>
          <div style={{ color: COL.kraft, marginTop: 4 }}>
            {`${hallado.resumen.productos} productos · ${hallado.resumen.ventas} ventas · ${hallado.resumen.cuentas} cuentas`}
          </div>
        </div>

        {cuantosHoy > 0 && (
          <div style={{ background: "#F7E4E0", border: `1.5px solid ${COL.chile}`, borderRadius: 12,
            padding: "11px 12px", marginTop: 11, fontSize: 12.5, fontWeight: 700,
            color: COL.chile, lineHeight: 1.45, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            {`Esto reemplaza lo que tienes ahora: ${cuantosHoy} ${cuantosHoy === 1 ? "producto" : "productos"} y sus ventas se van a perder. No se puede deshacer.`}
          </div>
        )}

        <button onClick={onOk}
          style={{ width: "100%", marginTop: 16, padding: 15, fontSize: 16, fontWeight: 800,
            background: cuantosHoy > 0 ? COL.chile : COL.billete, color: "#fff", border: "none",
            borderRadius: 14, cursor: "pointer", fontFamily: "inherit" }}>
          {cuantosHoy > 0 ? "Reemplazar todo con este respaldo" : "Restaurar"}
        </button>
        <button onClick={onCancel}
          style={{ width: "100%", marginTop: 8, padding: 12, fontSize: 14, fontWeight: 800,
            background: "transparent", color: COL.kraft, border: "none", borderRadius: 12,
            cursor: "pointer", fontFamily: "inherit" }}>
          Mejor no
        </button>
      </div>
    </div>
  );
}

export const moduloRespaldo = {
  id: "respaldo", nombre: "Respaldo a archivo", version: "1.0", tipo: "servicio",
  requiere: [],
  probar: () => {
    const estado = { nivel: "2.1", productos: [{ id: "a", apodo: "x" }], ventas: [],
      clientes: [{ id: "c" }], cuentas: [], gastos: [], cobro: { banco: "BBVA" }, secciones: [{ id: "s" }] };
    const r = armarRespaldo(estado, "Puesto de Danny");
    if (r.app !== "mi-negocio") return "el respaldo no se identifica";
    if (r.resumen.productos !== 1) return "el resumen no cuenta los productos";

    const v = leerRespaldo(JSON.stringify(r));
    if (!v.ok) return "no se pudo volver a leer lo que acaba de escribir";
    if (v.estado.nivel !== "2.1") return "se perdió el nivel al ir y volver";
    if (v.estado.cobro.banco !== "BBVA") return "se perdieron los datos de cobro";
    if (v.estado.productos.length !== 1) return "se perdieron los productos";

    // Basura de todo tipo: nunca debe tronar, siempre debe explicar.
    for (const malo of ["", "no es json", "[]", "null", '{"app":"otra-cosa"}',
                        '{"app":"mi-negocio"}', '{"app":"mi-negocio","datos":{}}']) {
      const m = leerRespaldo(malo);
      if (m.ok) return `aceptó un archivo que no debía: ${malo}`;
      if (!m.problema) return "rechazó sin decir por qué";
    }
    const futuro = leerRespaldo('{"app":"mi-negocio","version":99,"datos":{"productos":[]}}');
    if (futuro.ok) return "aceptó un respaldo de una versión más nueva";

    // Un respaldo manipulado no mete campos que no existen
    const sucio = leerRespaldo('{"app":"mi-negocio","version":1,"datos":{"productos":[],"maldad":"x"}}');
    if (!sucio.ok) return "rechazó uno que sí era válido";
    if ("maldad" in sucio.estado) return "dejó pasar un campo que no es del contrato";

    if (!/^mi-negocio-\d{4}-\d{2}-\d{2}\.json$/.test(nombreArchivo(new Date(2026, 9, 6))))
      return "el nombre del archivo no trae la fecha";
    return true;
  },
};
